import { useState, useMemo } from 'react';
import { Spreadsheet, SheetTabs } from '../../spreadsheet';
import { useModels } from '../../../lib/supabase/models/supabaseModelService';
import { useShifts } from '../../../lib/supabase/shifts/supabaseShiftService';
import { Card, CardContent } from '../../ui/card';
import { DateTime } from 'luxon';

interface ModelPPVData {
  model_id: string;
  modelName: string;
  total30Day: number;
  total7Day: number;
  group: 1 | 2;
  // Add projection tracking
  daysWorked30: number;
  daysWorked7: number;
  isProjected30Day: boolean;
  isProjected7Day: boolean;
}

interface WeeklyModelPPVData {
  model_id: string;
  modelName: string;
  weeks: number[]; // Array of weekly totals
  monthlyTotal: number;
  group: 1 | 2;
  weeklyProjections: boolean[]; // Array of projection flags for each week
  isProjectedMonthly: boolean;
}

interface WeekPeriod {
  weekNumber: number;
  startDate: DateTime;
  endDate: DateTime;
  label: string;
}

interface ModelGroup {
  groupNumber: 1 | 2;
  label: string;
  data: WeeklyModelPPVData[];
}

interface ModelPPVSpreadsheetRow {
  [key: string]: string; // Dynamic columns: A (Model Name), B (Week 1), C (Week 2), etc.
}

interface ModelPPVSpreadsheetProps {
  defaultMonth: number;
  defaultYear: number;
  isFullscreen?: boolean;
}

export function ModelPPVSpreadsheet({ defaultMonth, defaultYear, isFullscreen }: ModelPPVSpreadsheetProps) {
  const [activeTab, setActiveTab] = useState<string>('');

  // Auto-balancing function to create 3 groups with similar earnings and model counts
  const autoBalanceGroups = (modelPPVData: ModelPPVData[]): { [key: number]: ModelPPVData[] } => {
    // Filter eligible models (unpaused, >0 earnings)
    const eligibleModels = modelPPVData.filter(model => model.total30Day > 0);
    
    if (eligibleModels.length === 0) {
      return { 1: [], 2: [] };
    }

    // Sort by 30-day earnings (descending)
    const sortedModels = [...eligibleModels].sort((a, b) => b.total30Day - a.total30Day);
    
    // Initialize groups
    const groups = [
      [] as ModelPPVData[],
      [] as ModelPPVData[],
    ];

    // Calculate target counts for balanced groups
    const totalModels = sortedModels.length;
    const baseCount = Math.floor(totalModels / 2);
    const remainder = totalModels % 2;
    
    const targetCounts = [
      baseCount + (remainder > 0 ? 1 : 0),
      baseCount + (remainder > 1 ? 1 : 0),
    ];

    // Distribute models using a round-robin approach with earnings balancing
    let groupEarnings = [0, 0];
    
    for (const model of sortedModels) {
      // Find the group with the lowest earnings that hasn't reached its target count
      let bestGroup = 0;
      let lowestEarnings = groupEarnings[0];
      
      for (let groupIdx = 0; groupIdx < 2; groupIdx++) {
        if (groups[groupIdx].length < targetCounts[groupIdx]) {
          if (groupEarnings[groupIdx] < lowestEarnings) {
            lowestEarnings = groupEarnings[groupIdx];
            bestGroup = groupIdx;
          }
        }
      }
      
      // If all groups at target, find the one with lowest earnings
      if (groups[bestGroup].length >= targetCounts[bestGroup]) {
        bestGroup = 0;
        lowestEarnings = groupEarnings[0];
        for (let groupIdx = 0; groupIdx < 2; groupIdx++) {
          if (groupEarnings[groupIdx] < lowestEarnings) {
            lowestEarnings = groupEarnings[groupIdx];
            bestGroup = groupIdx;
          }
        }
      }
      
      // Assign model to the selected group
      groups[bestGroup].push({ ...model, group: (bestGroup + 1) as 1 | 2 });
      groupEarnings[bestGroup] += model.total30Day;
    }

    // Log group balance for debugging
    const totalEarnings = groupEarnings[0] + groupEarnings[1];
    const avgEarnings = totalEarnings / 2;
    
    console.log('Group Balance:', {
      group1: { 
        count: groups[0].length, 
        earnings: groupEarnings[0], 
        percentage: ((groupEarnings[0] / totalEarnings) * 100).toFixed(1) + '%',
        variance: ((Math.abs(groupEarnings[0] - avgEarnings) / avgEarnings) * 100).toFixed(1) + '%'
      },
      group2: { 
        count: groups[1].length, 
        earnings: groupEarnings[1], 
        percentage: ((groupEarnings[1] / totalEarnings) * 100).toFixed(1) + '%',
        variance: ((Math.abs(groupEarnings[1] - avgEarnings) / avgEarnings) * 100).toFixed(1) + '%'
      },
      totalEarnings,
      avgEarnings
    });

    return { 1: groups[0], 2: groups[1] };
  };

  // Calculate date ranges based on defaultMonth and defaultYear (6am based)
  const dateRanges = useMemo(() => {
    // Get the start of the month at 6am
    const monthStart = DateTime.fromObject({year: defaultYear, month: defaultMonth, day: 1, hour: 6, minute: 0, second: 0, millisecond: 0 }, { zone: 'UTC' });
    
    const monthEnd = monthStart.plus({ days: 1 });

    // Calculate week periods
    const weeks: WeekPeriod[] = [];
    let weekStart = monthStart;
    let weekNumber = 1;

    while (weekStart < monthEnd) {
      const weekEnd = weekStart.plus({ days: 7 });
      
      // Don't let week extend beyond month end
      const actualWeekEnd = weekEnd > monthEnd ? monthEnd : weekEnd;
      
      weeks.push({
        weekNumber,
        startDate: weekStart,
        endDate: actualWeekEnd,
        label: `Week ${weekNumber}`
      });
      
      weekStart = weekEnd;
      weekNumber++;
    }

    return {
      monthStart,
      monthEnd,
      weeks
    };
  }, [defaultMonth, defaultYear]);

  // Fetch data
  const { data: modelsData, isLoading: modelsLoading } = useModels();
  const models = useMemo(() => modelsData || [], [modelsData]);

  // Fetch shifts for the entire month period
  const shiftsFilters = useMemo(() => [
    { field: 'start_date_time', operator: 'gte' as const, value: dateRanges.monthStart.toISO() },
    { field: 'start_date_time', operator: 'lt' as const, value: dateRanges.monthEnd.toISO() },
    { field: 'submitted', operator: 'eq' as const, value: true }
  ], [dateRanges.monthStart, dateRanges.monthEnd]);

  const { data: shiftsData = [], isLoading: shiftsLoading } = useShifts({
    filters: shiftsFilters
  });

  const shifts = useMemo(() => shiftsData || [], [shiftsData]);

  // Calculate monthly groups (consistent across all weeks)
  const monthlyGroups = useMemo(() => {
    if (!models.length || !shifts.length) return { 1: [], 2: [] };

    // Calculate monthly PPV totals for group assignment
    const modelTotals = new Map<string, {
      totalMonthly: number;
      daysWorkedMonthly: Set<string>;
    }>();

    // Initialize all models
    models.forEach(model => {
      modelTotals.set(model.id, {
        totalMonthly: 0,
        daysWorkedMonthly: new Set()
      });
    });

    // Process shifts for monthly totals
    shifts.forEach(shift => {
      if (!shift.model_id || !shift.ppv_earned) return;

      const totals = modelTotals.get(shift.model_id);
      if (!totals) return;

      const shiftDate = shift.start_date_time;
      const dateString = shiftDate.toISO()!.split('T')[0];

      totals.totalMonthly += shift.ppv_earned;
      totals.daysWorkedMonthly.add(dateString);
    });

    // Convert to ModelPPVData for group calculation
    const modelPPVData: ModelPPVData[] = [];
    models.forEach(model => {
      const totals = modelTotals.get(model.id);
      if (!totals) return;

      if (model.name.includes('Test') || model.paused) {
        return;
      }

      const daysWorkedMonthly = totals.daysWorkedMonthly.size;
      const daysInMonth = dateRanges.monthStart.daysInMonth || 0;
      
      // Calculate projections for monthly data
      let projectedMonthly = totals.totalMonthly;
      let isProjectedMonthly = false;
      
      if (daysWorkedMonthly > 0 && daysWorkedMonthly < daysInMonth && totals.totalMonthly > 0) {
        projectedMonthly = (totals.totalMonthly / daysWorkedMonthly) * daysInMonth;
        isProjectedMonthly = true;
      }
      
      modelPPVData.push({
        model_id: model.id,
        modelName: model.name,
        total30Day: projectedMonthly,
        total7Day: 0, // Not used for group calculation
        group: 1, // Temporary
        daysWorked30: daysWorkedMonthly,
        daysWorked7: 0, // Not used
        isProjected30Day: isProjectedMonthly,
        isProjected7Day: false
      });
    });

    // Use existing auto-balance function to create consistent groups
    return autoBalanceGroups(modelPPVData);
  }, [models, shifts, dateRanges.monthStart, dateRanges.monthEnd]);

  // Process data for unified view
  const processedData = useMemo(() => {
    if (!models.length || !shifts.length) return [];

    const groups: ModelGroup[] = [
      { groupNumber: 1, label: 'Group 1', data: [] },
      { groupNumber: 2, label: 'Group 2', data: [] },
    ];

    [1, 2].forEach(groupNum => {
      const groupModels = monthlyGroups[groupNum] || [];
      
      groupModels.forEach(monthlyModel => {
        // Calculate totals for each week and month
        const modelTotals = new Map<string, {
          totalPeriod: number;
          daysWorkedPeriod: Set<string>;
        }>();

        // Initialize with month and each week
        const periods = [
          { id: 'month', start: dateRanges.monthStart, end: dateRanges.monthEnd },
          ...dateRanges.weeks.map(week => ({ id: week.label, start: week.startDate, end: week.endDate }))
        ];

        periods.forEach(period => {
          modelTotals.set(period.id, {
            totalPeriod: 0,
            daysWorkedPeriod: new Set()
          });
        });

        // Process shifts
        shifts.forEach(shift => {
          if (!shift.model_id || shift.model_id !== monthlyModel.model_id || !shift.ppv_earned) return;

          const shiftDate = shift.start_date_time;
          const dateString = shiftDate.toISO()!.split('T')[0];

          periods.forEach(period => {
            if (shiftDate >= period.start && shiftDate < period.end) {
              const totals = modelTotals.get(period.id);
              if (totals) {
                totals.totalPeriod += shift.ppv_earned;
                totals.daysWorkedPeriod.add(dateString);
              }
            }
          });
        });

        // Calculate weekly totals and projections (first 4 weeks + remaining weeks)
        const weeks: number[] = [];
        const weeklyProjections: boolean[] = [];

        // Process first 4 weeks
        for (let i = 0; i < 4; i++) {
          if (i < dateRanges.weeks.length) {
            const week = dateRanges.weeks[i];
            const weekTotals = modelTotals.get(week.label);
            if (!weekTotals) {
              weeks.push(0);
              weeklyProjections.push(false);
            } else {
              const daysWorked = weekTotals.daysWorkedPeriod.size;
              const weekDays = Math.ceil(week.endDate.diff(week.startDate, 'days').days);
              
              let weekTotal = weekTotals.totalPeriod;
              let isProjected = false;
              
              if (daysWorked > 0 && daysWorked < weekDays && weekTotals.totalPeriod > 0) {
                weekTotal = (weekTotals.totalPeriod / daysWorked) * weekDays;
                isProjected = true;
              }
              
              weeks.push(weekTotal);
              weeklyProjections.push(isProjected);
            }
          } else {
            weeks.push(0);
            weeklyProjections.push(false);
          }
        }

        // Calculate remaining weeks (Week 5+)
        let remainingWeeksTotal = 0;
        let hasRemainingProjections = false;

        for (let i = 4; i < dateRanges.weeks.length; i++) {
          const week = dateRanges.weeks[i];
          const weekTotals = modelTotals.get(week.label);
          if (weekTotals) {
            const daysWorked = weekTotals.daysWorkedPeriod.size;
            const weekDays = Math.ceil(week.endDate.diff(week.startDate, 'days').days);
            
            let weekTotal = weekTotals.totalPeriod;
            
            if (daysWorked > 0 && daysWorked < weekDays && weekTotals.totalPeriod > 0) {
              weekTotal = (weekTotals.totalPeriod / daysWorked) * weekDays;
              hasRemainingProjections = true;
            }
            
            remainingWeeksTotal += weekTotal;
          }
        }

        weeks.push(remainingWeeksTotal); // Week 5+ total
        weeklyProjections.push(hasRemainingProjections);

        // Calculate monthly total and projection
        const monthTotals = modelTotals.get('month');
        let monthlyTotal = 0;
        let isProjectedMonthly = false;

        if (monthTotals) {
          const daysWorked = monthTotals.daysWorkedPeriod.size;
          const monthDays = Math.ceil(dateRanges.monthEnd.diff(dateRanges.monthStart, 'days').days);
          
          monthlyTotal = monthTotals.totalPeriod;
          
          if (daysWorked > 0 && daysWorked < monthDays && monthTotals.totalPeriod > 0) {
            monthlyTotal = (monthTotals.totalPeriod / daysWorked) * monthDays;
            isProjectedMonthly = true;
          }
        }

        groups[groupNum - 1].data.push({
          model_id: monthlyModel.model_id,
          modelName: monthlyModel.modelName,
          weeks,
          monthlyTotal,
          group: groupNum as 1 | 2,
          weeklyProjections,
          isProjectedMonthly
        });
      });

      // Sort by monthly total
      groups[groupNum - 1].data.sort((a, b) => b.monthlyTotal - a.monthlyTotal);
    });

    return groups.filter(group => group.data.length > 0);
  }, [models, shifts, dateRanges, monthlyGroups]);

  // Initialize active tab when data changes
  if (processedData.length > 0 && !activeTab) {
    setActiveTab(processedData[0].label);
  }

  // Get active group data
  const activeGroupData = processedData.find(group => group.label === activeTab);

  // Process data for spreadsheet (single group)
  const { spreadsheetData, projectionMap } = useMemo(() => {
    if (!activeGroupData) return { spreadsheetData: [], projectionMap: new Map() };

    const rows: ModelPPVSpreadsheetRow[] = [];
    const projections = new Map<number, { [key: string]: boolean }>();

    // Add model data
    activeGroupData.data.forEach((model, index) => {
      const hasAnyProjections = model.weeklyProjections.some(p => p) || model.isProjectedMonthly;
      
      const row: ModelPPVSpreadsheetRow = {
        A: model.modelName + (hasAnyProjections ? '*' : ''),
        B: model.weeks[0]?.toFixed(2) || '0.00',
        C: model.weeks[1]?.toFixed(2) || '0.00',
        D: model.weeks[2]?.toFixed(2) || '0.00',
        E: model.weeks[3]?.toFixed(2) || '0.00',
        F: model.weeks[4]?.toFixed(2) || '0.00',
        G: model.monthlyTotal.toFixed(2)
      };
      rows.push(row);

      // Track projections for this row
      projections.set(index, {
        A: false, // Model name doesn't get blue (already has asterisk styling)
        B: model.weeklyProjections[0] || false,
        C: model.weeklyProjections[1] || false,
        D: model.weeklyProjections[2] || false,
        E: model.weeklyProjections[3] || false,
        F: model.weeklyProjections[4] || false,
        G: model.isProjectedMonthly || false
      });
    });

    // Calculate group totals
    const groupTotals = activeGroupData.data.reduce((totals, model) => ({
      week1: totals.week1 + (model.weeks[0] || 0),
      week2: totals.week2 + (model.weeks[1] || 0),
      week3: totals.week3 + (model.weeks[2] || 0),
      week4: totals.week4 + (model.weeks[3] || 0),
      week5Plus: totals.week5Plus + (model.weeks[4] || 0),
      monthly: totals.monthly + model.monthlyTotal
    }), { week1: 0, week2: 0, week3: 0, week4: 0, week5Plus: 0, monthly: 0 });

    // Check if group has any projections
    const groupHasProjections = activeGroupData.data.some(model => 
      model.weeklyProjections.some(p => p) || model.isProjectedMonthly
    );

    // Add gap row
    const gapRow: ModelPPVSpreadsheetRow = {};
    ['A', 'B', 'C', 'D', 'E', 'F', 'G'].forEach(col => {
      gapRow[col] = '';
    });
    rows.push(gapRow);
    projections.set(rows.length - 1, {}); // Gap row has no projections

    // Add group total row
    const totalRow: ModelPPVSpreadsheetRow = {
      A: `TOTAL` + (groupHasProjections ? '*' : ''),
      B: groupTotals.week1.toFixed(2),
      C: groupTotals.week2.toFixed(2),
      D: groupTotals.week3.toFixed(2),
      E: groupTotals.week4.toFixed(2),
      F: groupTotals.week5Plus.toFixed(2),
      G: groupTotals.monthly.toFixed(2)
    };
    rows.push(totalRow);
    projections.set(rows.length - 1, {}); // Total row has no projections

    return { spreadsheetData: rows, projectionMap: projections };
  }, [activeGroupData]);

  // Fixed column headers
  const columnHeaders = ['Model Name', 'Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5+', 'Month'];

  // Fixed column count
  const columnCount = 7;

  // Pre-calculate row indices for performance
  const { totalRowIndex, gapRowIndex } = useMemo(() => {
    const dataLength = spreadsheetData.length;
    return {
      totalRowIndex: dataLength - 1,
      gapRowIndex: dataLength - 2
    };
  }, [spreadsheetData]);

  // Cell styling function
  const getCellStyle = (rowIdx: number, colKey: string, value: any) => {
    const isTotalRow = rowIdx === totalRowIndex;
    const isGapRow = rowIdx === gapRowIndex;

    // Style gap row
    if (isGapRow) {
      return {
        backgroundColor: '#f9fafb',
        borderBottom: '1px solid #e5e7eb'
      };
    }

    // Style totals row
    if (isTotalRow) {
      if (colKey === 'A') {
        const hasProjections = typeof value === 'string' && value.includes('*');
        
        return {
          fontWeight: 700,
          backgroundColor: '#f3f4f6',
          borderTop: '2px solid #374151',
          ...(hasProjections && {
            fontStyle: 'italic',
            color: '#2563eb'
          })
        };
      } else {
        return {
          fontWeight: 700,
          textAlign: 'right' as const,
          backgroundColor: '#f3f4f6',
          borderTop: '2px solid #374151',
          color: '#ef4444'
        };
      }
    }

    // Style model name column
    if (colKey === 'A' && !isTotalRow && !isGapRow) {
      const isProjected = typeof value === 'string' && value.includes('*');
      
      return {
        fontWeight: 500,
        ...(isProjected && {
          fontStyle: 'italic',
          color: '#2563eb'
        })
      };
    }

    // Style currency columns
    if (['B', 'C', 'D', 'E', 'F', 'G'].includes(colKey) && !isTotalRow && !isGapRow) {
      // Check if this specific cell is projected
      const rowProjections = projectionMap.get(rowIdx);
      const isProjected = rowProjections?.[colKey] || false;

      const isZeroValue = (val: any): boolean => {
        if (typeof val === 'string') {
          return parseFloat(val) === 0;
        }
        return val === 0;
      };

      if (isZeroValue(value)) {
        return {
          color: isProjected ? '#60a5fa' : '#9ca3af', // Light blue for projected zero values
          fontSize: '12px',
          textAlign: 'right' as const,
          fontWeight: 500,
          ...(isProjected && { fontStyle: 'italic' })
        };
      }

      return {
        textAlign: 'right' as const,
        fontWeight: 500,
        ...(isProjected && {
          color: '#2563eb', // Blue for projected values
          fontStyle: 'italic'
        })
      };
    }

    return {
      fontSize: '12px'
    };
  };

  // Check if any data has projections for legend display
  const hasAnyProjections = useMemo(() => {
    return processedData.some(group =>
      group.data.some(model => model.weeklyProjections.some(p => p) || model.isProjectedMonthly)
    );
  }, [processedData]);

  // Calculate loading state
  const isLoading = modelsLoading || shiftsLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading model PPV data...</div>
      </div>
    );
  }

  if (processedData.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No PPV data found for the selected period
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div className="flex flex-col">
            {activeGroupData && (
              <Spreadsheet
                columns={columnCount}
                minRows={Math.max(spreadsheetData.length, 20)}
                data={spreadsheetData}
                editable={false}
                columnWidths={[200, ...Array(columnCount - 1).fill(120)]}
                rowHeight={35}
                showColumnHeaders={true}
                customColumnHeaders={columnHeaders}
                fillHeight={true}
                isFullscreen={isFullscreen}
                buffers={{ fullscreen: 90, normal: 90 }}
                getCellStyle={getCellStyle}
                csvFilename={`model-ppv-${defaultYear}-${(defaultMonth).toString().padStart(2, '0')}.csv`}
                csvIncludeHeaders={true}
                className="rounded-b-none"
              />
            )}

            {/* Group tabs */}
            <div className="border-t">
              <SheetTabs
                tabs={processedData.map(group => ({
                  id: group.label,
                  label: group.label
                }))}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Legend for projected values */}
      {hasAnyProjections && (
        <div className="text-xs text-muted-foreground italic">
          * Projected earnings based on available shift data
        </div>
      )}
    </div>
  );
}

export default ModelPPVSpreadsheet; 